import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { StarFavorite } from "@/lib/helpers/customIcons";
import { getThreadChatPath } from "@/lib/helpers/threadChatPath";
import { ThreadEntity } from "@/lib/types/Thread";
import { TextInput } from "@/lib/components/ui/TextInput/TextInput";
import { Badge } from "@/lib/components/ui/Badge/Badge";
import { ConfirmationModal } from "@/lib/components/ConfirmationModal/ConfirmationModal";
import { OptionsModal } from "@/lib/components/ui/OptionsModal/OptionsModal";
import { Option } from "@/lib/types/Options";

import styles from "./ThreadItem.module.scss";
import { useChatsListItem } from "../../hooks/useChatsListItem";
import { useMarkThreadAsRead } from "../../hooks/useMarkThreadAsRead";
import { ThreadStatusIcon } from "@/lib/components/ui/ThreadStatusIcon/ThreadStatusIcon";

type ThreadItemProps = {
  chatHistoryItem: ThreadEntity;
  agentName?: string;
  isUnread?: boolean;
};

export const ThreadItem = ({
  chatHistoryItem,
  agentName,
  isUnread,
}: ThreadItemProps): JSX.Element => {
  const {
    chatName,
    setChatName,
    editingName,
    selected,
    isFavorite,
    toggleFavorite,
    startEditing,
    finishEditing,
    cancelEditing,
    deleteChat,
  } = useChatsListItem(chatHistoryItem);

  const navigate = useNavigate();
  const { t } = useTranslation(["chat"]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const markThreadAsRead = useMarkThreadAsRead();

  useEffect(() => {
    if (selected && isUnread) {
      void markThreadAsRead(chatHistoryItem.chat_id);
    }
  }, [
    selected,
    isUnread,
    chatHistoryItem.chat_id,
    chatHistoryItem.last_message_at,
    markThreadAsRead,
  ]);

  const menuOptions: Option[] = [
    {
      label: t("rename", { ns: "chat", defaultValue: "Rename" }),
      iconName: "edit",
      iconColor: "white",
      onClick: () => {
        startEditing();
      },
    },
    {
      label: t("delete", { ns: "chat", defaultValue: "Delete" }),
      iconName: "delete",
      iconColor: "white",
      onClick: () => {
        setIsDeleteModalOpen(true);
      },
    },
  ];

  const handleStarClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    void toggleFavorite();
  };

  const handleItemClick = () => {
    const chatPath = getThreadChatPath({
      threadId: chatHistoryItem.chat_id,
      assistantId: chatHistoryItem.assistant_id,
    });
    if (chatPath && !editingName && !isDeleteModalOpen) {
      void navigate(chatPath);
    }
  };

  return (
    <div
      className={`${styles.thread_item} ${selected ? styles.selected : ""}`}
      onClick={handleItemClick}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          handleItemClick();
        }
      }}
    >
      <span
        className={`${styles.star_icon} ${
          isFavorite ? styles.star_active : ""
        }`}
        onClick={handleStarClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            void toggleFavorite();
          }
        }}
      >
        <StarFavorite />
      </span>
      {editingName ? (
        <div
          className={styles.name_input_wrapper}
          onClick={(e) => e.stopPropagation()}
        >
          <TextInput
            inputValue={chatName}
            setInputValue={setChatName}
            small
            simple
            className={styles.name_input}
            onSubmit={finishEditing}
            inputProps={{
              autoFocus: true,
              onChange: (e) => setChatName(e.target.value),
              onBlur: finishEditing,
              onKeyDown: (e) => {
                if (e.key === "Enter") {
                  finishEditing();
                }
                if (e.key === "Escape") {
                  cancelEditing();
                }
              },
            }}
          />
        </div>
      ) : (
        <span
          className={`${styles.thread_name} ${
            isUnread && !selected ? styles.unread : ""
          }`}
          title={chatName.trim()}
        >
          {chatName.trim()}
        </span>
      )}

      {agentName && <Badge name={agentName} color="outline" />}
      {chatHistoryItem.status ? (
        <ThreadStatusIcon status={chatHistoryItem.status} animated />
      ) : (
        <span className={styles.status_placeholder} aria-hidden="true" />
      )}
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <OptionsModal options={menuOptions} iconColor="inherit" />
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setOpen={setIsDeleteModalOpen}
        onConfirm={() => {
          setIsDeleteModalOpen(false);
          void deleteChat();
        }}
        title={t("deleteChatConfirmation", {
          ns: "chat",
          defaultValue: "Are you sure you want to delete this chat?",
        })}
      />
    </div>
  );
};
